/** Base HTTP client for the vuln-sentinel API.
 *
 * Attaches the OKTA access token as `Authorization: Bearer <token>`. The token getter
 * is injected (set once from the OKTA auth context) so this module stays React-free.
 * In no-auth local mode the getter returns null and no Authorization header is sent
 * (the deployed API requires auth; local dev typically points at a dev API or mocks).
 */
import { getApiBaseUrl } from "@/common/utilities/env";

type TokenGetter = () => string | null | undefined;

let _getToken: TokenGetter = () => null;

/** Wire the token source (called once from the auth provider). */
export function setTokenGetter(fn: TokenGetter): void {
  _getToken = fn;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const base = getApiBaseUrl();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const token = _getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const resp = await fetch(`${base}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const text = await resp.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!resp.ok) {
    const msg =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : resp.statusText) || `HTTP ${resp.status}`;
    throw new ApiError(resp.status, msg);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string, body?: unknown) => request<T>("DELETE", path, body),
};

/** Build a query string from defined params (skips empty/undefined). */
export function qs(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== "",
  );
  if (!entries.length) return "";
  return (
    "?" +
    entries
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v!)}`)
      .join("&")
  );
}
